import { useCallback, useEffect, useRef, useState } from 'react';
import useSWR from 'swr';

import { getLinkTo, getTemporalProperty } from '@rikstv/play-common/src/components/swimlane/swimlane.utils';
import { mapToSwimlaneItem } from '@rikstv/play-common/src/forces/assetDetails/swimlaneItemMapper';
import { TvSeriesEpisode } from '@rikstv/play-common/src/forces/tvSeries/tvSeriesDetails.types';
import { request } from '@rikstv/play-common/src/forces/utils/request';
import { useAuthToken } from '@rikstv/play-common/src/hooks/useAuthToken';

import { GenericSeason } from '../../contentStack.types';
import { useOnModalPlayerClosed } from '../../hooks/useModalPlayerLogic';

const PAGE_SIZE = 25;
export const useSeasonEpisodes = (season: GenericSeason) => {
  const token = useAuthToken();
  const [episodes, setEpisodes] = useState<TvSeriesEpisode[]>([]);
  const { loadNextPage, seasonLink, pageNr } = usePaginatedLink(season);
  const hasMoreEpisodes = episodes.length > 0 && episodes.length < season.episodeCount;

  const { data, isLoading, mutate } = useSWR(seasonLink, url => episodeFetcher(url, token), {
    revalidateOnFocus: false,
  });
  // refresh season when modal player closes
  useOnModalPlayerClosed(mutate);

  useEffect(() => {
    if (data) {
      if (pageNr > 1) {
        setEpisodes(current => Array.from(new Set([...current, ...data])));
      } else {
        setEpisodes(data);
      }
    }
  }, [data, pageNr, token]);

  return {
    episodes,
    isLoading,
    hasMoreEpisodes,
    loadMoreEpisodes: loadNextPage,
    pageNr,
  };
};

const episodeFetcher = async (url: string, token: string | null): Promise<TvSeriesEpisode[]> => {
  const res = await request<EpisodesResponse>({ url }, token);
  return res.items.map((card): TvSeriesEpisode => {
    const item = mapToSwimlaneItem(card, { loggedIn: token != null, style: 'Standard', type: 'Default' });

    return {
      id: item.id,
      programState: item.programState,
      title: item.title,
      description: item.description,
      image: item.image.url,
      progress: { percent: item.userData?.progressRatio ?? 0 },
      subtitle: item.subtitle,
      episodeLink: getLinkTo(item),
      behavior: getTemporalProperty(item.behaviors),
      isPlayable: false,
      logos: item.logos,
      _links: card._links,
    };
  });
};

const usePaginatedLink = (season: GenericSeason) => {
  const [page, setPage] = useState<number>(1);
  // reset page to 1 when season change
  useEffect(() => setPage(1), [season.link]);
  const lastUpdatedTimestamp = useRef(0);

  const url = new URL(season.link);
  url.searchParams.append('pageSize', PAGE_SIZE.toString());
  if (season.episodeCount > PAGE_SIZE) {
    url.searchParams.append('page', page.toString());
  }

  const loadNextPage = useCallback(() => {
    // Avoid multiple load statements by adding a timeout
    // Should not be able to scroll 30 element in that time
    const now = Date.now();
    const deltaTime = Math.abs(now - lastUpdatedTimestamp.current);
    if (deltaTime > 3_000) {
      setPage(p => p + 1);
      lastUpdatedTimestamp.current = now;
    }
  }, []);

  return {
    pageNr: page,
    seasonLink: url.toString(),
    loadNextPage,
  };
};

interface EpisodesResponse {
  items: RiksTV.ClientAPI.SwimlaneCard[];
  pageSize: number;
  page: number;
  hasMorePages: boolean;
}
